// Roadmap.tsx (v4) — PWA V2 slice 3. Leads with the Plan Health causal hero
// (Pattern 3, PlanHealthData type) fed by LIVE roadmap data from
// /api/pwa/heroes: velocity (7d closes vs prior 7d, anchored at the scrub
// point when time-shifted) and the phase-ETA projection.
// Below the hero: consolidated phase closure display (projection + close
// distance + predicted closure), then phase/wave timeline, milestone drill-down.

import { useCallback, useEffect, useRef, useState } from "react";
import { api, getPhaseProgress, fetchEntities } from "../../api/client.js";
import type { HeroesResponse, PhaseInfo, WaveGroup, PhaseProgressResponse } from "../../api/types.js";
import { PlanHealthHero } from "../../components/Heroes.js";
import { PhaseTimeline, WaveTimeline } from "../../screens/Roadmap.js";
import { PhaseCloseTile } from "../../components/PhaseCloseTile.js";
import { PredictedClosureTile } from "../../components/PredictedClosureTile.js";
import { useTimeShift } from "../TimeShift.js";
import { ConnectivityBanner } from "../../components/ConnectivityBanner.js";


// ─── Step status badge color ────────────────────────────────────────────────
// STEP-OV-ROADMAP-DRILLDOWN: status badges on steps with semantic color coding.
// complete=green, in_progress/in-progress=blue, blocked=red, proposed/pending=gray.

function stepStatusColor(status: string): string {
  const s = status.toLowerCase().replace(/-/g, "_");
  if (s === "complete" || s === "completed") return "var(--ok)";
  if (s === "in_progress" || s === "running" || s === "active") return "var(--accent)";
  if (s === "blocked" || s === "failed") return "var(--danger)";
  // proposed, pending, spec_only, verify_only, etc. → gray
  return "var(--text-dim)";
}

// ─── MilestoneDrillDown ────────────────────────────────────────────────────
// Single-column expandable list of milestones with their child steps.
// Each milestone row is click-through to the entity sheet; expanding reveals
// child steps which are also click-through.
// Steps are lazy-loaded per milestone on expand via stepsByMilestone map.

interface MilestoneDrillDownProps {
  milestones: Record<string, unknown>[];
  stepsByMilestone: Map<string, Record<string, unknown>[]>;
  loadingMilestones: Set<string>;
  milestoneErrors: Set<string>;
  expanded: Set<string>;
  onToggle: (id: string) => void;
}

function MilestoneDrillDown({
  milestones,
  stepsByMilestone,
  loadingMilestones,
  milestoneErrors,
  expanded,
  onToggle,
}: MilestoneDrillDownProps) {
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--text-dim)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          padding: "16px 16px 4px",
        }}
      >
        Milestones
      </div>
      <div style={{ padding: "0 12px 8px" }}>
        {milestones.map((ms) => {
          const msId = String(ms.id ?? ms.milestone_id ?? "");
          const msTitle = String(ms.title ?? ms.milestone_title ?? "");
          const msStatus = String(ms.status ?? "");
          const msPct = ms.completion_pct != null ? Number(ms.completion_pct) : null;
          const isExpanded = expanded.has(msId);
          const isLoading = loadingMilestones.has(msId);
          const childSteps = stepsByMilestone.get(msId) ?? [];

          return (
            <div key={msId} style={{ marginBottom: 6 }}>
              {/* Milestone row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "var(--bg-elev)",
                  border: "1px solid var(--border-soft)",
                  borderRadius: isExpanded ? "8px 8px 0 0" : 8,
                  padding: "10px 12px",
                  cursor: "pointer",
                }}
                onClick={() => onToggle(msId)}
              >
                {/* Expand indicator */}
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    color: "var(--text-dim)",
                    minWidth: 10,
                  }}
                >
                  {isExpanded ? "▾" : "▸"}
                </span>

                {/* Milestone ID — click-through */}
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-dim)",
                    minWidth: 64,
                  }}
                  onClick={(e: { stopPropagation: () => void }) => {
                    e.stopPropagation();
                    window.__openEntity?.("milestones", msId);
                  }}
                >
                  {msId}
                </span>

                {/* Title */}
                <span
                  style={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--text)",
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {msTitle}
                </span>

                {/* Status */}
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 9,
                    color: "var(--text-dim)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {msStatus}
                </span>

                {/* Completion % */}
                {msPct != null && (
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      fontWeight: 700,
                      color: msPct < 50 ? "var(--danger)" : msPct < 100 ? "var(--warn)" : "var(--ok, var(--text-muted))",
                      minWidth: 36,
                      textAlign: "right",
                    }}
                  >
                    {msPct > 0 ? `${msPct}%` : (msStatus || `${msPct ?? 0}%`)}
                  </span>
                )}
              </div>

              {/* Step rows — shown when expanded */}
              {isExpanded && (
                <div
                  style={{
                    border: "1px solid var(--border-soft)",
                    borderTop: "none",
                    borderRadius: "0 0 8px 8px",
                    overflow: "hidden",
                  }}
                >
                  {isLoading ? (
                    <div
                      style={{
                        padding: "8px 12px 8px 32px",
                        fontSize: 11,
                        color: "var(--text-dim)",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      loading…
                    </div>
                  ) : childSteps.length === 0 ? (
                    milestoneErrors.has(msId) ? (
                      <div style={{ padding: "8px 12px 8px 32px", fontSize: 11, color: "var(--danger)", fontFamily: "var(--font-mono)" }}>
                        Failed to load steps — tap to retry
                      </div>
                    ) : (
                      <div style={{ padding: "8px 12px 8px 32px", fontSize: 11, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>
                        no steps linked
                      </div>
                    )
                  ) : (
                    childSteps.map((s) => {
                      const sId = String(s.id ?? s.step_id ?? "");
                      const sTitle = String(s.title ?? "");
                      const sStatus = String(s.status ?? "");
                      return (
                        <button
                          key={sId}
                          onClick={() => window.__openEntity?.("steps", sId)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            width: "100%",
                            textAlign: "left",
                            background: "transparent",
                            border: "none",
                            borderTop: "1px solid var(--border-soft)",
                            padding: "8px 12px 8px 30px",
                            cursor: "pointer",
                            fontFamily: "inherit",
                          }}
                        >
                          <span
                            style={{
                              fontFamily: "var(--font-mono)",
                              fontSize: 10,
                              fontWeight: 700,
                              color: "var(--text-dim)",
                              minWidth: 64,
                            }}
                          >
                            {sId}
                          </span>
                          <span
                            style={{
                              flex: 1,
                              fontSize: 12,
                              color: "var(--text)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {sTitle}
                          </span>
                          <span
                            style={{
                              fontFamily: "var(--font-mono)",
                              fontSize: 9,
                              color: stepStatusColor(sStatus),
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              fontWeight: 700,
                              padding: "1px 4px",
                              borderRadius: 3,
                              background: `color-mix(in srgb, ${stepStatusColor(sStatus)} 12%, transparent)`,
                              flexShrink: 0,
                            }}
                          >
                            {sStatus}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RoadmapScreen() {
  const { asOf, isLive } = useTimeShift();
  const [heroes, setHeroes] = useState<HeroesResponse | null>(null);
  const [waveGroups, setWaveGroups] = useState<WaveGroup[]>([]);
  const [phases, setPhases] = useState<PhaseInfo[]>([]);
  const [phaseProgress, setPhaseProgress] = useState<PhaseProgressResponse | null>(null);
  const [milestones, setMilestones] = useState<Record<string, unknown>[]>([]);
  const [loadedSteps, setLoadedSteps] = useState<Map<string, Record<string, unknown>[]>>(new Map());
  const [milestoneErrors, setMilestoneErrors] = useState<Set<string>>(new Set());
  const [loadingMilestones, setLoadingMilestones] = useState<Set<string>>(new Set());
  const [expandedMilestones, setExpandedMilestones] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const inFlight = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const [h, dashboard, progress, msResult] = await Promise.all([
        api.heroes(asOf),
        // The dashboard (phase/wave timeline) has no temporal archive — live.
        api.dashboard().catch(() => null),
        // Phase progress tiles — best-effort, non-fatal if unavailable
        getPhaseProgress().catch(() => null),
        // Milestone drill-down — best-effort, non-fatal
        fetchEntities("milestones").catch(() => null),
      ]);
      setHeroes(h);
      setWaveGroups(dashboard?.wave?.groups ?? []);
      setPhases(dashboard?.phase?.phases ?? []);
      setPhaseProgress(progress);
      // Show all milestones from entity graph (phases 1-6 pending migration — VLR-1150 issue 5)
      const allMilestones = msResult?.entities ?? [];
      setMilestones(allMilestones);
      setLoadError(null);
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoaded(true);
    }
  }, [asOf]);

  useEffect(() => {
    void load();
    // A scrubbed (historical) view is frozen — no point polling it.
    if (!isLive) return;
    const interval = setInterval(() => void load(), 30_000);
    return () => clearInterval(interval);
  }, [load, isLive]);

  const handleToggle = useCallback(async (id: string) => {
    setExpandedMilestones((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); return next; }
      next.add(id); return next;
    });
    // Guard using ref — synchronous, not subject to React batching
    // Note: milestoneErrors.has(id) is NOT in the guard, so errors are retried on re-expand
    if (loadedSteps.has(id) || inFlight.current.has(id)) return;
    // Clear any prior error for this milestone before attempting fetch
    setMilestoneErrors((prev: Set<string>) => { const next = new Set(prev); next.delete(id); return next; });
    inFlight.current.add(id);
    setLoadingMilestones((prev) => new Set(prev).add(id));
    try {
      const result = await fetchEntities("steps", { milestoneId: id });
      setLoadedSteps((prev) => new Map(prev).set(id, result.entities));
      setMilestoneErrors((prev: Set<string>) => { const next = new Set(prev); next.delete(id); return next; });
    } catch {
      // Do NOT store [] in loadedSteps — so the guard allows retry on re-expand
      setMilestoneErrors((prev: Set<string>) => new Set(prev).add(id));
    } finally {
      inFlight.current.delete(id);
      setLoadingMilestones((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  }, [loadedSteps]);

  if (!loaded && !loadError) {
    return (
      <div
        style={{
          padding: "40px 20px",
          textAlign: "center",
          color: "var(--text-dim)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
        }}
      >
        Loading roadmap…
      </div>
    );
  }

  if (loadError && !heroes) {
    return <ConnectivityBanner error={loadError} variant="block" />;
  }

  return (
    <>
      {loadError != null && <ConnectivityBanner error={loadError} variant="inline" />}

      {!isLive && (
        <div
          style={{
            padding: "8px 20px",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--text-dim)",
          }}
        >
          ◷ velocity as of scrub point — projection & timeline are LIVE
        </div>
      )}

      <PlanHealthHero
        data={heroes?.planHealth}
        onNavigate={(intent) => {
          if (intent.subpath) window.__openEntity?.("milestones", intent.subpath);
        }}
      />

      {/* Phase closure — consolidated just below the header (STEP-PWA-ROADMAP-SIMPLIFY).
          Projection (target/planned/now from heroes), close distance, and predicted
          closure are three views of one question: when does this phase close. */}
      {(heroes?.planHealth?.projection != null || phaseProgress != null) && (
        <>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--text-dim)",
              padding: "12px 16px 4px",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Phase Closure
          </div>

          {/* Projection row (from heroes/planHealth velocity model) */}
          {heroes?.planHealth?.projection != null && (() => {
            const proj = heroes.planHealth!.projection!;
            const sev = heroes.planHealth!.severity;
            const nowColor = sev === "alert" ? "var(--danger)" : sev === "attention" ? "var(--warn)" : sev === "watch" ? "var(--accent)" : "var(--ok)";
            return (
              <div
                style={{
                  margin: "0 8px 8px",
                  padding: "10px 14px",
                  background: "var(--bg-elev)",
                  border: "1px solid var(--border-soft)",
                  borderRadius: 8,
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 8,
                }}
              >
                {([
                  { label: "Target", val: proj.target, c: "var(--text)" },
                  { label: "Planned", val: proj.planned, c: "var(--text-muted)" },
                  { label: "Now", val: proj.now, c: nowColor },
                ] as const).map((col) => (
                  <div key={col.label}>
                    <div style={{ fontSize: 9, color: "var(--text-dim)", fontFamily: "var(--font-mono)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>
                      {col.label}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: col.c }}>
                      {col.val}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Phase close distance + cohort split + burn rate + milestone trajectories */}
          {phaseProgress != null && (
            <>
              <PhaseCloseTile data={phaseProgress} />
              <PredictedClosureTile data={phaseProgress} />
            </>
          )}
        </>
      )}

      {phases.length > 0 && (
        <>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-dim)", padding: "12px 20px 0", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Phase history · % = step completion
          </div>
          <PhaseTimeline
            phases={([...new Map(phases.map((p: PhaseInfo) => [p.number, p])).values()] as PhaseInfo[])
              .sort((a: PhaseInfo, b: PhaseInfo) => a.number - b.number)}
          />
        </>
      )}
      {waveGroups.length > 0 && <WaveTimeline groups={waveGroups} />}

      {/* Milestone → step drill-down (lazy-loaded steps per milestone) */}
      {/* Always show the migration note — VLR-1150 issue 5: visible even when 0 milestones loaded */}
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-dim)", padding: "4px 16px", marginBottom: 0 }}>
        {milestones.length > 0
          ? "Phases 1–6 not yet in entity graph · showing entity graph milestones only"
          : "Milestones from phases 1–6 not yet migrated to entity graph · not shown"}
      </div>
      {milestones.length > 0 && (
        <>
          <MilestoneDrillDown
            milestones={milestones}
            stepsByMilestone={loadedSteps}
            loadingMilestones={loadingMilestones}
            milestoneErrors={milestoneErrors}
            expanded={expandedMilestones}
            onToggle={handleToggle}
          />
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-dim)", padding: "4px 16px 12px" }}>
            Phases 1–6 milestones pending migration to entity graph · not shown
          </div>
        </>
      )}

      <div style={{ height: 80 }} />
    </>
  );
}
